/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { SegmentCreator } from './SegmentCreator';
import { CloseIcon } from '@icons/CloseIcon';
import { PlayIconFill } from '@icons/PlayIconFill';
import { Button } from '@components/Button';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import {
  ALL_GARM_CATEGORIES,
  ROOT_SEGMENT_ID,
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { useInsightsParams } from '@insights_module/hooks/useInsightsParams';
import { DoubleArrowToRight } from '@icons/DoubleArrowToRight';
import { genRandomString, tokenToObject } from '@utils/commonUtils';
import { createQueryDataFromQuery } from '@insights_module/adc_ql/utils';
import { VideoPlayer } from '@insights_module/components/VideoPlayer';
import { Stars, StarsWhite } from '@icons/Stars';
import { ArrowToRight } from '@icons/ArrowToRight';
import { Modal } from '@components/Modal';
import { Loader } from '@components/Loader';
import { useOpenAIContext } from '@insights_module/context/OpenAIContext';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { setTextAreaHeight } from '@utils/dom';
import { useNotification } from '@insights_module/hooks/useNotification';
import { RefetchIcon } from '@icons/RefetchIcon';
import { TrashCanIcon } from '@icons/TrashCanIcon';
import { useGenerateAQLFilter } from '@insights_module/hooks/useGenerateAQLFilter';

const Container = tw.div`flex flex-col relative w-full lg:min-w-[1200px] max-w-[1200px] border border-solid border-gray-1 rounded-lg bg-neutral-background overflow-visible shadow-modal`;
const Header = tw.div`w-full bg-neutral-background px-6 py-2 border-b border-solid border-gray-1 flex items-center justify-between rounded-t-lg`;
const Title = tw.span`text-sm text-text-and-icon text-left font-semibold`;
const Footer = tw.div`flex py-[7px] px-6 justify-between border-t border-gray-1 bg-neutral-background rounded-b-lg`;
const ModalButton = tw(
  Button.Primary
)`text-xs leading-none p-0 w-auto h-[28px] border border-solid border-accent-2 bg-accent-2 text-text-and-icon align-middle shadow-none`;
const ButtonsWrapper = tw.div`flex items-center justify-end gap-2`;
const ControlButton = tw(
  Button.Primary
)`text-xs leading-none px-3 py-[5px] w-auto h-[28px] border border-solid border-accent-2 bg-accent-2 align-middle shadow-none`;
const SideBarOuterWrapper = tw.div`flex flex-col gap-4 p-4 pr-6 min-h-[450px] min-w-[296px] max-w-[296px] border-l border-gray-1 shadow-dropdown overflow-hidden transition-all duration-300`;
const SideBarInnerWrapper = tw.div`flex h-full w-full gap-2`;
const SideBarArrowWrapper = tw.div`h-full`;
const SideBarContentWrapper = tw.div`flex flex-col gap-2 max-h-[300px] w-full`;
const SideBarTitle = tw.span`block text-[11px] text-text-and-icon font-semibold`;
const SideBarSubTitle = tw.span`block text-[11px] text-text-and-icon-muted-2 pb-1`;
const SideBarItem = tw.span`p-2 items-center self-stretch border border-gray-3 bg-page-background rounded text-xs text-text-and-icon cursor-pointer hover:opacity-80`;
const VideoContainer = tw.div`absolute w-full h-full p-6 bg-neutral-background z-50 flex items-center justify-center`;
const VideoToggler = tw(
  Button.Secondary
)`bg-accent-2 border border-solid border-accent-2 text-xs py-[7px] px-6 leading-none h-auto shadow-ui flex items-center justify-center text-text-and-icon-invert`;
const PlayIconWrapper = tw.div`bg-accent-2 rounded-full h-4 w-4 flex items-center justify-center mr-2`;
const ExampleListTitle = tw.span`text-text-and-icon-muted-2 text-left font-semibold group-hover:text-text-and-icon`;
const ExampleList = tw.ul`pl-2 list-inside not-last:mb-1.5`;
const ExampleListItem = tw.li`list-disc text-text-and-icon group-hover:text-text-and-icon-muted-2`;
const HeaderLeft = tw.div`flex gap-[12px] items-center`;
const AIButton = tw.div`flex gap-[4px] bg-sec-4 rounded p-[4px] pr-[8px] text-[11px] text-dark-accent-invert cursor-pointer`;
const AIInputOuterWrapper = tw.div`flex p-[4px] justify-center items-center rounded border border-accent-2 bg-sec-4 overflow-hidden transition-all duration-300`;
const AIInputInnerWrapper = tw.div`w-full h-full flex justify-center items-end p-[4px] pl-[12px] gap-[8px] rounded border border-gray-1 bg-neutral-background`;
const SubmitMessageButton = tw.div`flex items-center justify-center min-w-[54px] min-h-[24px] cursor-pointer text-xs italic gap-[4px] bg-gray-2 px-[8px] rounded text-accent-muted`;
const UserMessage = tw.div`relative flex rounded bg-sec-4 p-[8px] text-xs text-text-and-icon`;
const UserMessageIconsOuterWrapper = tw.div`absolute top-0 right-0 w-[65px] h-[32px] flex justify-end items-center`;
const UserMessageOverlay = tw.div`absolute w-full h-full bg-sec-4 opacity-50 z-0`;
const UserMessageIconsWrapper = tw.div`flex gap-[2px] bg-sec-4`;
const UserMessageIconWrapper = tw.div`flex justify-center items-center rounded w-[24px] h-[24px] cursor-pointer hover:bg-gray-3`;
const AIInput = tw.textarea`w-full h-full bg-transparent outline-none placeholder:text-sm text-sm resize-none`;

const getSegmentDataSet1 = () =>
  [
    { id: ROOT_SEGMENT_ID, type: 'all', groupId: null, negation: false },
    {
      id: genRandomString(16),
      type: 'brand_safety_garm',
      category: 'all-garm-categories',
      condition: 'range',
      value: { gte: 'unrelated', lte: 'low' },
      groupId: ROOT_SEGMENT_ID,
      negation: false,
    },
  ] as SegmentCreatorData[];

const getSegmentDataSet2 = () =>
  [
    { id: ROOT_SEGMENT_ID, type: 'all', groupId: null, negation: false },
    {
      id: genRandomString(16),
      type: 'brand_safety_garm',
      category: 'crime_harmful_act',
      condition: 'range',
      value: { gte: 'unrelated', lte: 'low' },
      groupId: ROOT_SEGMENT_ID,
      negation: false,
    },
    {
      id: genRandomString(16),
      type: 'brand_safety_garm',
      category: 'arms_ammunition',
      condition: 'range',
      value: { gte: 'unrelated', lte: 'low' },
      groupId: ROOT_SEGMENT_ID,
      negation: false,
    },
    {
      id: genRandomString(16),
      type: 'brand_safety_garm',
      category: 'all-garm-categories',
      condition: 'range',
      value: { gte: 'unrelated', lte: 'high' },
      groupId: ROOT_SEGMENT_ID,
      negation: false,
    },
    {
      id: genRandomString(16),
      type: 'contextual',
      condition: 'includes',
      value: 'Healthy Living',
      groupId: ROOT_SEGMENT_ID,
      negation: false,
      category: null,
    },
    {
      id: genRandomString(16),
      type: 'named_entities',
      condition: 'excludes',
      value: 'Oprah Winfrey',
      groupId: ROOT_SEGMENT_ID,
      negation: false,
      category: null,
    },
  ] as SegmentCreatorData[];

// we need to create new id on every example click, otherwise react will not re-render the component
// that is why we have getData function instead of just data
const examples = [
  {
    lists: [
      {
        title: (
          <Trans key='insights.query_builder.examples.example_1.list_1.title'>
            Brand Suitability
          </Trans>
        ),
        items: [
          <Trans key='insights.query_builder.examples.example_1.list_1.list_item_1'>
            All categories likely unrelated or lower
          </Trans>,
        ],
      },
    ],
    getData: getSegmentDataSet1,
  },
  {
    lists: [
      {
        title: (
          <Trans key='insights.query_builder.examples.example_2.list_1.title'>
            Brand Suitability
          </Trans>
        ),
        items: [
          <Trans key='insights.query_builder.examples.example_2.list_1.list_item_1'>
            Crime: unrelated to likely unrelated
          </Trans>,
          <Trans key='insights.query_builder.examples.example_2.list_1.list_item_2'>
            Arms: unrelated to likely unrelated
          </Trans>,
          <Trans key='insights.query_builder.examples.example_2.list_1.list_item_3'>
            All other categories: unrelated to likely related
          </Trans>,
        ],
      },
      {
        title: (
          <Trans key='insights.query_builder.examples.example_2.list_2.title'>
            IAB Content Categories
          </Trans>
        ),
        items: [
          <Trans key='insights.query_builder.examples.example_2.list_2.list_item_1'>
            Include: healthy living
          </Trans>,
        ],
      },
      {
        title: (
          <Trans key='insights.query_builder.examples.example_2.list_3.title'>Named Entities</Trans>
        ),
        items: [
          <Trans key='insights.query_builder.examples.example_2.list_3.list_item_1'>
            Exclude: Oprah Winfrey
          </Trans>,
        ],
      },
    ],
    getData: getSegmentDataSet2,
  },
];

const SHOW_FILTER_EXAMPLES = 'show_filter_examples';
const tutorialURL = `${import.meta.env.VITE_AUTH_API_CDN_URL}/tutorials/query-builder.mp4`;

const SideBar = ({
  onVideoToggle,
  sendMessage,
  isLoading,
}: {
  onVideoToggle: () => void;
  sendMessage: (message: string) => Promise<void> | void;
  isLoading: boolean;
}) => {
  const showFilterExamples = JSON.parse(
    localStorage.getItem(SHOW_FILTER_EXAMPLES) || 'true'
  ) as boolean;
  const { isEnabled: isOpenAIPocEnabled } = useFeatureFlag(FEATURE_FLAG.OPEN_AI_POC);
  const { t } = useTranslation();

  const { segmentsData, setSegmentsData } = useSegmentCreatorContext();
  const { shouldShowAIChat } = useInsightsContext();
  const { userMessages, addUserMessage, removeUserMessage } = useOpenAIContext();
  const [isCollapsed, setIsCollapsed] = useState(!showFilterExamples);
  const [selectedExampleIndex, setSelectedExampleIndex] = useState<number>(-1);
  const [hoveredUserMessageIndex, setHoveredUserMessageIndex] = useState<number>(-1);

  const handleSideBarItemClick = (data: SegmentCreatorData[]) => {
    setSegmentsData(data);
  };

  const handleSelectUserMessage = (message: string) => {
    if (isLoading) return;
    sendMessage(message);
    addUserMessage(message);
  };

  const handleDeleteUserMessage = (index: number) => {
    removeUserMessage(index);
  };

  useEffect(() => {
    if (segmentsData.length) {
      // because we are creating new id on every example click, we need to compare data without id
      const index = examples.findIndex(
        ({ getData }) =>
          JSON.stringify(getData().map(({ id, ...rest }) => ({ ...rest }))) ===
          JSON.stringify(segmentsData.map(({ id, ...rest }) => ({ ...rest })))
      );
      setSelectedExampleIndex(index);
    }
  }, [segmentsData]);

  useEffect(() => {
    localStorage.setItem(SHOW_FILTER_EXAMPLES, JSON.stringify(!isCollapsed));
  }, [isCollapsed]);

  return (
    <SideBarOuterWrapper css={[isCollapsed && tw`max-w-[56px] min-w-[56px] h-auto`]}>
      <SideBarInnerWrapper>
        <SideBarArrowWrapper>
          <DoubleArrowToRight
            tw='fill-text-and-icon w-4 h-4'
            onClick={() => setIsCollapsed(!isCollapsed)}
            css={[isCollapsed && tw`rotate-180`]}
          />
        </SideBarArrowWrapper>
        <SideBarContentWrapper css={[isCollapsed && tw`hidden h-full`]}>
          <div>
            <SideBarTitle>
              {shouldShowAIChat && isOpenAIPocEnabled
                ? t('insights.query_builder.ai.sidebar_title')
                : t('insights.query_builder.examples.title')}
            </SideBarTitle>
            {!shouldShowAIChat && (
              <SideBarSubTitle>{t('insights.query_builder.examples.sub_title')}</SideBarSubTitle>
            )}
          </div>
          {shouldShowAIChat && isOpenAIPocEnabled ? (
            <>
              {userMessages.map((message, index) => (
                <UserMessage
                  css={[isLoading && !index && tw`bg-gray-2`]}
                  key={index}
                  onMouseEnter={() => setHoveredUserMessageIndex(index)}
                  onMouseLeave={() => setHoveredUserMessageIndex(-1)}>
                  {message}
                  {/* show action buttons on prompt hover, but if it's not prompt for which we currently fetching data */}
                  {hoveredUserMessageIndex === index && ((!isLoading && !index) || !!index) && (
                    <UserMessageIconsOuterWrapper>
                      <UserMessageOverlay />
                      <UserMessageIconsWrapper tw='z-10 relative pr-[4px]'>
                        <UserMessageIconWrapper onClick={() => handleSelectUserMessage(message)}>
                          <RefetchIcon tw='fill-text-and-icon-muted' />
                        </UserMessageIconWrapper>
                        <UserMessageIconWrapper onClick={() => handleDeleteUserMessage(index)}>
                          <TrashCanIcon tw='stroke-text-and-icon-muted' />
                        </UserMessageIconWrapper>
                      </UserMessageIconsWrapper>
                    </UserMessageIconsOuterWrapper>
                  )}
                </UserMessage>
              ))}
            </>
          ) : (
            <>
              {examples.map((example, index) => (
                <SideBarItem
                  className='group'
                  key={index}
                  onClick={() => handleSideBarItemClick(example.getData())}
                  css={[selectedExampleIndex === index && tw`border-gray-1 bg-gray-3 shadow-ui`]}>
                  {example.lists.map((list, listIndex) => (
                    <Fragment key={listIndex}>
                      <ExampleListTitle
                        css={[selectedExampleIndex === index && tw`text-text-and-icon-muted-2`]}>
                        {list.title}
                      </ExampleListTitle>
                      <ExampleList>
                        {list?.items?.map((item, itemIndex) => (
                          <ExampleListItem
                            css={[selectedExampleIndex === index && tw`text-text-and-icon`]}
                            key={itemIndex}>
                            {item}
                          </ExampleListItem>
                        ))}
                      </ExampleList>
                    </Fragment>
                  ))}
                </SideBarItem>
              ))}
              <VideoToggler onClick={onVideoToggle}>
                <PlayIconWrapper>
                  <PlayIconFill tw='h-3 fill-text-and-icon-invert' />
                </PlayIconWrapper>
                {t('insights.query_builder.examples.video_cta')}
              </VideoToggler>
            </>
          )}
        </SideBarContentWrapper>
      </SideBarInnerWrapper>
    </SideBarOuterWrapper>
  );
};

export const FiltersModal = (): JSX.Element | null => {
  const { isEnabled: isOpenAIPocEnabled } = useFeatureFlag(FEATURE_FLAG.OPEN_AI_POC);
  const { t } = useTranslation();
  const { NTFC_TYPE, notify } = useNotification();
  const { showFiltersModal, setShowFiltersModal, shouldShowAIChat, setShouldShowAIChat } =
    useInsightsContext();
  const { queryToken, setSegmentsData } = useSegmentCreatorContext();
  const { setQueryToken } = useInsightsParams();
  const { addUserMessage } = useOpenAIContext();
  const { sendMessage, isLoading, result } = useGenerateAQLFilter();
  const [isVideoVisible, setIsVideoVisible] = useState<boolean>(false);
  const [isAiInputFocused, setIsAiInputFocused] = useState<boolean>(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const tokenRef = useRef<string | null>(queryToken);

  const handleCloseModal = () => {
    try {
      // we should always have token, but just in case
      const query = tokenToObject(tokenRef.current as string);
      const segmentsData = createQueryDataFromQuery([query]);
      setSegmentsData(segmentsData);
    } catch {
      setShowFiltersModal(false);
    }
    setShowFiltersModal(false);
  };

  const handleApplyFilters = () => {
    setQueryToken(queryToken);
    setShowFiltersModal(false);
  };

  const handleReset = () => {
    setSegmentsData([
      { id: ROOT_SEGMENT_ID, groupId: null, type: 'all', negation: false },
      {
        id: genRandomString(16),
        groupId: ROOT_SEGMENT_ID,
        type: 'brand_safety_garm',
        category: ALL_GARM_CATEGORIES,
        negation: false,
      },
    ]);
  };

  const handleSendMessage = async () => {
    if (!textareaRef.current?.value) return;

    sendMessage(textareaRef.current.value);
    addUserMessage(textareaRef.current.value);
    textareaRef.current.value = '';
  };

  const handleEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (showFiltersModal) {
      tokenRef.current = queryToken;
    }
  }, [showFiltersModal]);

  useEffect(() => {
    if (!result) return;

    if (result === 'error') {
      notify({ type: NTFC_TYPE.DANGER, message: t('insights.query_builder.ai.error_message') });
      return;
    }

    const segmentsData = createQueryDataFromQuery([result]);
    setSegmentsData(segmentsData);
  }, [result]);

  if (!showFiltersModal) {
    return null;
  }

  return (
    <Modal onClose={handleCloseModal}>
      <Container>
        <Header>
          <HeaderLeft>
            <Title>{t('common.filters')}</Title>
            {isOpenAIPocEnabled && (
              <>
                <div tw='h-[20px] w-[1px] bg-gray-1' />
                <AIButton
                  css={{
                    background: shouldShowAIChat
                      ? 'linear-gradient(103deg, #54B5D3 0%, #2132A0 97.92%)'
                      : 'var(--sec-4)',
                    color: shouldShowAIChat ? '#fff' : 'var(--dark-accent-invert)',
                  }}
                  onClick={() => setShouldShowAIChat(!shouldShowAIChat)}>
                  {shouldShowAIChat ? <StarsWhite tw='w-[16px]' /> : <Stars tw='w-[16px]' />}
                  {t('insights.query_builder.ai.title')}
                </AIButton>
              </>
            )}
          </HeaderLeft>
          <ModalButton tw='bg-transparent border-none w-[28px]' onClick={handleCloseModal}>
            <CloseIcon tw='fill-text-and-icon' />
          </ModalButton>
        </Header>

        <div tw='flex'>
          <div tw='flex flex-col p-6 w-full'>
            {isOpenAIPocEnabled && (
              <AIInputOuterWrapper css={[!shouldShowAIChat && tw`h-0 p-0 opacity-0`]}>
                <AIInputInnerWrapper>
                  {!isAiInputFocused && <Stars tw='w-[24px]' />}
                  <AIInput
                    disabled={isLoading}
                    ref={textareaRef}
                    onFocus={() => setIsAiInputFocused(true)}
                    onBlur={() => setIsAiInputFocused(false)}
                    onInput={(e) => setTextAreaHeight(e.target as HTMLTextAreaElement)}
                    rows={1}
                    placeholder={t('insights.query_builder.ai.placeholder')}
                    onKeyDown={(e) =>
                      handleEnterPress(e as React.KeyboardEvent<HTMLTextAreaElement>)
                    }
                  />
                  {isLoading ? (
                    <Loader size={'xs'} theme={'light'} />
                  ) : (
                    <SubmitMessageButton onClick={handleSendMessage}>
                      {t('common.enter')}
                      <ArrowToRight tw='w-[16px] h-[16px] fill-accent-muted' />
                    </SubmitMessageButton>
                  )}
                </AIInputInnerWrapper>
              </AIInputOuterWrapper>
            )}

            <SegmentCreator />
          </div>

          <SideBar
            onVideoToggle={() => setIsVideoVisible(!isVideoVisible)}
            sendMessage={sendMessage}
            isLoading={isLoading}
          />
        </div>
        <Footer>
          <ControlButton
            tw='bg-transparent border-gray-1 text-text-and-icon-muted'
            onClick={handleReset}>
            {t('common.reset')}
          </ControlButton>
          <ButtonsWrapper>
            <ControlButton
              tw='bg-transparent border-gray-1 text-text-and-icon-muted'
              onClick={handleCloseModal}>
              {t('common.cancel')}
            </ControlButton>
            <ControlButton tw='border-accent-2 w-[90px]' onClick={handleApplyFilters}>
              {t('common.apply')}
            </ControlButton>
          </ButtonsWrapper>
        </Footer>

        {isVideoVisible && (
          <VideoContainer>
            <ModalButton
              tw='bg-transparent border-text-and-icon-muted h-8 w-8 absolute z-50 top-2 right-2 flex items-center justify-center'
              onClick={() => setIsVideoVisible(!isVideoVisible)}>
              <CloseIcon tw='fill-text-and-icon-muted' />
            </ModalButton>
            <VideoPlayer src={tutorialURL} outerProgressbar />
          </VideoContainer>
        )}
      </Container>
    </Modal>
  );
};
